import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const W = 960, H = 420;
const project = ([lon, lat]) => [((lon + 180) / 360) * W, ((90 - lat) / 180) * H * 0.98];
const ringPath = (ring) => ring.map((pt, i) => (i ? "L" : "M") + project(pt).map((n) => n.toFixed(1)).join(",")).join("") + "Z";
const geomPath = (g) =>
  g.type === "Polygon" ? g.coordinates.map(ringPath).join(" ") : g.coordinates.map((poly) => poly.map(ringPath).join(" ")).join(" ");

const flagEmoji = (cc) => {
  if (!cc || cc.length !== 2 || cc === "??") return "🌍";
  try { return String.fromCodePoint(...cc.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0))); } catch { return ""; }
};

const STAT_ROWS = [
  ["orders", "Commandes créées"],
  ["paid", "✅ Ventes payées"],
  ["revenue", "💶 Encaissé (€)"],
  ["mollie_redirects", "Redirections Mollie (web)"],
  ["mollie_paid", "Mollie payés"],
  ["gp_attempts", "Clics paiement Google Play (app)"],
  ["gp_paid", "Google Play payés"],
  ["app_attempts", "Tentatives via app Android"],
  ["web_attempts", "Tentatives via web"],
  ["payment_started", "🕐 Paiement commencé non fini"],
  ["abandoned", "👻 Paniers abandonnés"],
  ["technical_error", "⚠️ Erreurs techniques"],
  ["device_errors", "Erreurs signalées par l'appareil"],
  ["subs_attempts", "Abonnements tentés"],
  ["subs_active", "Abonnements actifs"],
  ["clients", "Clients distincts"],
];

export const AdminSalesMap = ({ days }) => {
  const [geo, setGeo] = useState(null);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    fetch("/world-countries.json").then((r) => r.json()).then(setGeo).catch(() => {});
  }, []);
  useEffect(() => {
    setData(null);
    api.get(`/admin/sales-map?days=${days}`).then(({ data }) => setData(data)).catch(() => {});
  }, [days]);

  const byCC = useMemo(() => {
    const m = {};
    (data?.countries || []).forEach((c) => { m[c.country_code] = c; });
    return m;
  }, [data]);

  const fillFor = (cc) => {
    const c = byCC[cc];
    if (!c) return "#71717a";
    if (c.paid > 0 || c.subs_active > 0 || c.mollie_paid > 0 || c.gp_paid > 0) return "#16a34a";
    const attempts = c.mollie_redirects + c.gp_attempts;
    if (attempts > 0 || c.technical_error > 0) return "#dc2626";
    return "#d97706";
  };

  const paths = useMemo(() => {
    if (!geo) return [];
    return geo.features.map((f) => ({ iso: f.properties.iso, name: f.properties.name, d: geomPath(f.geometry) }));
  }, [geo]);

  const active = selected ? byCC[selected] : null;
  const unknown = byCC["??"];

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="admin-sales-map">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <p className="font-heading text-sm font-semibold">🗺️ Carte des ventes par pays</p>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#16a34a]" /> A payé</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#dc2626]" /> A tenté de payer sans réussir</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#d97706]" /> Commande sans clic paiement</span>
        </div>
        <p className="ml-auto text-[10px] text-muted-foreground">Cliquez sur un pays pour tout le détail</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-[1fr_290px]">
        <div className="relative overflow-hidden rounded-lg border border-border bg-background/50">
          {!geo || !data ? (
            <p className="p-10 text-center text-xs text-muted-foreground">Chargement de la carte...</p>
          ) : (
            <svg viewBox={`0 40 ${W} ${H - 80}`} className="h-auto w-full">
              {paths.map((p) => {
                const c = byCC[p.iso];
                return (
                  <path key={p.iso + p.name} d={p.d} fill={fillFor(p.iso)}
                    fillOpacity={c ? 0.9 : 0.22} stroke="hsl(var(--border))" strokeWidth="0.5"
                    className={c ? "cursor-pointer transition-opacity hover:opacity-70" : ""}
                    data-testid={c ? `map-country-${p.iso}` : undefined}
                    onMouseEnter={() => c && setHover({ iso: p.iso, name: p.name })}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => c && setSelected(selected === p.iso ? null : p.iso)} />
                );
              })}
            </svg>
          )}
          {hover && byCC[hover.iso] && (
            <div className="pointer-events-none absolute left-3 top-3 rounded-lg border border-border bg-card px-3 py-2 text-[11px] shadow-lg">
              <p className="font-semibold">{flagEmoji(hover.iso)} {byCC[hover.iso].country || hover.name}</p>
              <p className="text-muted-foreground">
                {byCC[hover.iso].paid} payée(s) · {byCC[hover.iso].revenue}€ · {byCC[hover.iso].orders} commande(s) · {byCC[hover.iso].abandoned} abandon(s)
              </p>
            </div>
          )}
        </div>
        <div className="max-h-[340px] space-y-2 overflow-y-auto pr-1">
          {active ? (
            <div className="rounded-lg border border-primary/40 bg-primary/5 p-3" data-testid="map-country-detail">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold">{flagEmoji(active.country_code)} {active.country}</p>
                <button onClick={() => setSelected(null)} className="text-[10px] text-muted-foreground hover:text-foreground">✕ Fermer</button>
              </div>
              <div className="space-y-1">
                {STAT_ROWS.map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-semibold">{k === "revenue" ? `${active[k]}€` : active[k]}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <p className="text-[11px] font-semibold text-muted-foreground">Classement ({(data?.countries || []).filter((c) => c.country_code !== "??").length} pays)</p>
              {(data?.countries || []).filter((c) => c.country_code !== "??").map((c) => (
                <button key={c.country_code} onClick={() => setSelected(c.country_code)} data-testid={`map-rank-${c.country_code}`}
                  className="flex w-full items-center justify-between rounded-lg border border-border px-2.5 py-1.5 text-left text-[11px] hover:border-primary/50">
                  <span>{flagEmoji(c.country_code)} {c.country}</span>
                  <span className="text-muted-foreground">
                    <span className={c.paid ? "font-semibold text-success" : ""}>{c.paid}✓</span> · {c.revenue}€ · {c.orders + c.subs_attempts} cmd
                  </span>
                </button>
              ))}
              {unknown && (
                <button onClick={() => setSelected("??")} data-testid="map-rank-unknown"
                  className="flex w-full items-center justify-between rounded-lg border border-dashed border-border px-2.5 py-1.5 text-left text-[11px] text-muted-foreground hover:border-primary/50">
                  <span>🌍 Pays non identifié (comptes test / sans géo)</span>
                  <span>{unknown.orders + unknown.subs_attempts} cmd</span>
                </button>
              )}
              {data && !(data.countries || []).length && <p className="text-[11px] text-muted-foreground">Aucune donnée sur la période.</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
